import { metisTechnical } from '../metis-proxy/MetisTechnical'
import * as crypto from 'crypto'
import { WebsiteCode } from './tour-operator-types'
import { cmClient } from '../../legacy/contentmanager-util'


export const ContactInfoType = {
	NAME: 'Name',
	EMAIL: 'E-Mail',
	PHONE: 'Phone',
	ADDRESS: 'Address',
	WEBSITE: 'Website',
}
/**
 * Tour Operator Utility Methods
 * 
 * This class contains all utility methods, lookups, validation helpers,
 * and low-level database operations for the Tour Operator domain.
 */
export class TourOperatorUtil {

	// =================================================================
	// SYSTEM LOOKUPS & ID RESOLVERS
	// =================================================================

	/**
	 * Get customer group ID by group name
	 */
	static async getCustomerGroupIdByName(metisId: string, groupName: string): Promise<number> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT customer_group_id
			FROM customer_group
			WHERE customer_group_code = ?
		`, [groupName])
		
		if (!result || result.length === 0) {
			throw new Error(`Customer group not found: ${groupName}`)
		}
		
		return result[0].customer_group_id
	}

	/**
	 * Get website ID by website name
	 */
	static async getWebsiteIdByCode(metisId: string, websiteCode: WebsiteCode): Promise<number> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT website_id
			FROM core_website
			WHERE code = ?
		`, [websiteCode])
		
		if (!result || result.length === 0) {
			throw new Error(`Website not found: ${websiteCode}`)
		}
		
		return result[0].website_id
	}

	/**
	 * Get role ID by role name
	 */
	static async getRoleIdByName(metisId: string, roleName: string): Promise<number> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT id
			FROM jfb_b2bportal_role
			WHERE name = ?
		`, [roleName])
		
		if (!result || result.length === 0) {
			throw new Error(`Role not found: ${roleName}`)
		}
		
		return result[0].id
	}

	/**
	 * Get entity type ID by entity code
	 */
	static async getEntityTypeIdByCode(metisId: string, entityCode: string): Promise<number> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT entity_type_id
			FROM eav_entity_type
			WHERE entity_type_code = ?
		`, [entityCode])

		if (!result || result.length === 0) {
			throw new Error(`Entity type not found: ${entityCode}`)
		}

		return result[0].entity_type_id
	}

	/**
	 * Get B2B account type ID by name (for EAV attribute value)
	 */
	static async getB2BAccountTypeIdByName(metisId: string, accountTypeName: string): Promise<number> {
		// First get the attribute ID for b2bportal_accounttype
		const attributeResult = await metisTechnical.queryMetis(metisId, `
			SELECT a.attribute_id
			FROM eav_attribute a
			JOIN eav_entity_type et ON et.entity_type_id = a.entity_type_id
			WHERE et.entity_type_code = 'customer' AND a.attribute_code = 'b2bportal_accounttype'
		`, [])
		
		if (!attributeResult || attributeResult.length === 0) {
			throw new Error('B2B portal account type attribute not found')
		}
		
		const attributeId = attributeResult[0].attribute_id
		
		// Then get the option ID for the account type name
		const optionResult = await metisTechnical.queryMetis(metisId, `
			SELECT o.option_id
			FROM eav_attribute_option o
			JOIN eav_attribute_option_value ov ON ov.option_id = o.option_id
			WHERE o.attribute_id = ? AND ov.value = ? AND ov.store_id = 0
		`, [attributeId, accountTypeName])
		
		if (!optionResult || optionResult.length === 0) {
			throw new Error(`B2B account type option not found: ${accountTypeName}`)
		}
		
		return optionResult[0].option_id
	}

	// =================================================================
	// EXISTENCE VALIDATORS
	// =================================================================

	/**
	 * Check if customer exists by email (globally across all websites)
	 */
	static async customerExistsByEmail(metisId: string, email: string): Promise<boolean> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT entity_id
			FROM customer_entity
			WHERE email = ?
		`, [email])
		
		return result && result.length > 0
	}

	/**
	 * Check if customer exists by email on a specific website
	 */
	static async customerExistsByEmailAndWebsite(metisId: string, email: string, websiteId: number): Promise<boolean> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT entity_id
			FROM customer_entity
			WHERE email = ? AND website_id = ?
		`, [email, websiteId])
		
		return result && result.length > 0
	}

	/**
	 * Check if B2B Partner exists by customer ID
	 */
	static async b2bPartnerExistsByCustomerId(metisId: string, customerId: number): Promise<boolean> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT id
			FROM jfb_b2bportal_partner
			WHERE customer_id = ?
		`, [customerId])
		
		return result && result.length > 0
	}

	/**
	 * Check if contract exists by contract ID
	 */
	static async contractExistsById(metisId: string, contractId: number): Promise<boolean> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT id
			FROM jfb_b2bportal_contract
			WHERE id = ?
		`, [contractId])
		
		return result && result.length > 0
	}

	// =================================================================
	// EAV ATTRIBUTE HELPERS
	// =================================================================

	/**
	 * Set a varchar EAV attribute for a customer
	 */
	static async setCustomerVarcharAttribute(metisId: string, customerId: number, entityTypeId: number, attributeCode: string, value: string): Promise<void> {
		// Get attribute ID
		const attributeResult = await metisTechnical.queryMetis(metisId, `
			SELECT attribute_id
			FROM eav_attribute
			WHERE entity_type_id = ? AND attribute_code = ?
		`, [entityTypeId, attributeCode])
		
		if (!attributeResult || attributeResult.length === 0) {
			// context.log.warn(`Attribute ${attributeCode} not found for entity type ${entityTypeId}`)
			return
		}
		
		const attributeId = attributeResult[0].attribute_id
		
		await metisTechnical.queryMetis(metisId, `
			INSERT INTO customer_entity_varchar (
				entity_type_id,
				attribute_id,
				entity_id,
				value
			) VALUES (?, ?, ?, ?)
			ON DUPLICATE KEY UPDATE value = VALUES(value)
		`, [entityTypeId, attributeId, customerId, value])
	}

	/**
	 * Set an int EAV attribute for a customer
	 */
	static async setCustomerIntAttribute(metisId: string, customerId: number, entityTypeId: number, attributeCode: string, value: number): Promise<void> {
		// Get attribute ID
		const attributeResult = await metisTechnical.queryMetis(metisId, `
			SELECT attribute_id
			FROM eav_attribute
			WHERE entity_type_id = ? AND attribute_code = ?
		`, [entityTypeId, attributeCode])
		
		if (!attributeResult || attributeResult.length === 0) {
			// context.log.warn(`Attribute ${attributeCode} not found for entity type ${entityTypeId}`)
			return
		}
		
		const attributeId = attributeResult[0].attribute_id
		
		await metisTechnical.queryMetis(metisId, `
			INSERT INTO customer_entity_int (
				entity_type_id,
				attribute_id,
				entity_id,
				value
			) VALUES (?, ?, ?, ?)
			ON DUPLICATE KEY UPDATE value = VALUES(value)
		`, [entityTypeId, attributeId, customerId, value])
	}

	/**
	 * Get all address attributes with their metadata (id, code, backend_type)
	 * Returns a map keyed by attribute_code for easy lookup
	 */
	static async getAddressAttributeMap(metisId: string, entityTypeId: number): Promise<Record<string, any>> {
		const attributesResult = await metisTechnical.queryMetis(metisId, `
			SELECT attribute_id, attribute_code, backend_type
			FROM eav_attribute
			WHERE entity_type_id = ?
		`, [entityTypeId])
		
		const attributeMap: Record<string, any> = {}
		
		for (const attr of attributesResult) {
			attributeMap[attr.attribute_code] = {
				attribute_id: attr.attribute_id,
				attribute_code: attr.attribute_code,
				backend_type: attr.backend_type
			}
		}
		
		return attributeMap
	}

	/**
	 * Batch upsert address attribute values
	 * Groups by backend_type and executes one query per type for efficiency
	 * Inspired by package-designer-service.ts upsertAttributeValues pattern
	 */
	static async upsertAddressAttributeValues(
		metisId: string,
		entityTypeId: number,
		addressId: number,
		attributeValues: Array<{
			attribute: {
				attribute_id: number
				code: string
				backend_type: string
			}
			value: string
		}>
	): Promise<void> {
		// Group attributes by backend_type
		const groupedByType: Record<string, Array<{
			attribute: {
				attribute_id: number
				code: string
				backend_type: string
			}
			value: string
		}>> = {}
		
		for (const av of attributeValues) {
			const backendType = av.attribute.backend_type
			if (!groupedByType[backendType]) {
				groupedByType[backendType] = []
			}
			groupedByType[backendType].push(av)
		}
		
		// Execute batch insert/update for each backend type
		for (const backendType in groupedByType) {
			const avs = groupedByType[backendType]
			
			// Build multi-row insert with ON DUPLICATE KEY UPDATE
			const tableName = `customer_address_entity_${backendType}`
			
			// Build values for batch insert
			const valueRows: string[] = []
			const queryParams: any[] = []
			
			for (const av of avs) {
				valueRows.push('(?, ?, ?, ?)')
				queryParams.push(
					entityTypeId,
					av.attribute.attribute_id,
					addressId,
					av.value
				)
			}
			
			// Execute batch upsert
			const query = `
				INSERT INTO ${tableName}
					(entity_type_id, attribute_id, entity_id, value)
				VALUES ${valueRows.join(', ')}
				ON DUPLICATE KEY UPDATE value = VALUES(value)
			`
			
			await metisTechnical.queryMetis(metisId, query, queryParams)
		}
	}

	// =================================================================
	// PASSWORD AND SECURITY UTILITIES
	// =================================================================

	/**
	 * Simple hash function for passwords
	 */
	static hashPassword(password: string): string {
		return crypto.createHash('md5').update(password).digest('hex')
	}

	// =================================================================
	// SIMPLE CRUD OPERATIONS
	// =================================================================

	/**
	 * Update contract status
	 */
	static async updateContractStatus(metisId: string, contractId: number, isActive: boolean): Promise<void> {
		// context.log.info(`Updating contract ${contractId} status to ${isActive}`)
		
		await metisTechnical.queryMetis(metisId, `
			UPDATE jfb_b2bportal_contract
			SET active = ?
			WHERE id = ?
		`, [isActive ? 1 : 0, contractId])
		
		// context.log.info(`Successfully updated contract status`)
	}

	/**
	 * Update commission rate for contract-category relationship
	 */
	static async updateContractCategoryCommission(metisId: string, contractId: number, categoryId: number, commissionRate: number): Promise<void> {
		// context.log.info(`Updating commission rate for contract ${contractId} and category ${categoryId} to ${commissionRate}`)
		
		await metisTechnical.queryMetis(metisId, `
			UPDATE jfb_b2bportal_contract2category
			SET commission_rate = ?
			WHERE contract_id = ? AND category_id = ?
		`, [commissionRate, contractId, categoryId])
		
		// context.log.info(`Successfully updated commission rate`)
	}

	/**
	 * Update contract end date
	 */
	static async updateContractEndDate(metisId: string, contractId: number, endDate: string): Promise<void> {
		await metisTechnical.queryMetis(metisId, `
			UPDATE jfb_b2bportal_contract
			SET end = ?
			WHERE id = ?
		`, [endDate, contractId])
	}

	/**
	 * Find PEAK contracts by customer email
	 */
	static async findContractsByCustomerEmail(metisId: string, email: string): Promise<Array<{contractId: number, partnerId: number, active: number, startDate: string, endDate: string}>> {
		const contracts = await metisTechnical.queryMetis(metisId, `
			SELECT 
				c.id as contractId,
				c.partner_id as partnerId,
				c.active,
				c.start as startDate,
				c.end as endDate
			FROM jfb_b2bportal_contract c
			JOIN jfb_b2bportal_partner p ON p.id = c.partner_id
			JOIN customer_entity ce ON ce.entity_id = p.customer_id
			WHERE ce.email = ?
		`, [email])

		return contracts || []
	}

	/**
	 * Get contract-category relationships for a contract
	 */
	static async getContractCategories(metisId: string, contractId: number): Promise<Array<{categoryId: number, commissionRate: number, individualSale: boolean, presalePeriodDays: number | null}>> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT 
				category_id as categoryId,
				commission_rate as commissionRate,
				individual_sale as individualSale,
				presale_period_days as presalePeriodDays
			FROM jfb_b2bportal_contract2category
			WHERE contract_id = ?
		`, [contractId])
		
		return result.map(row => ({
			categoryId: row.categoryId,
			commissionRate: parseFloat(row.commissionRate),
			individualSale: row.individualSale === 1,
			presalePeriodDays: row.presalePeriodDays
		}))
	}

	// =================================================================
	// DEBUGGING HELPERS
	// =================================================================

	/**
	 * Get all customer attributes for debugging
	 */
	static async getAllCustomerAttributes(metisId: string): Promise<Array<{attributeId: number, attributeCode: string, backendType: string}>> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT a.attribute_id as attributeId, a.attribute_code as attributeCode, a.backend_type as backendType
			FROM eav_attribute a
			JOIN eav_entity_type et ON et.entity_type_id = a.entity_type_id
			WHERE et.entity_type_code = 'customer'
			ORDER BY a.attribute_code
		`, [])
		
		return result || []
	}

	/**
	 * Get all B2B account type options for debugging
	 */
	static async getAllB2BAccountTypeOptions(metisId: string): Promise<Array<{optionId: number, value: string}>> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT o.option_id as optionId, ov.value
			FROM eav_attribute_option o
			JOIN eav_attribute_option_value ov ON ov.option_id = o.option_id
			JOIN eav_attribute a ON a.attribute_id = o.attribute_id
			JOIN eav_entity_type et ON et.entity_type_id = a.entity_type_id
			WHERE et.entity_type_code = 'customer' AND a.attribute_code = 'b2bportal_accounttype' AND ov.store_id = 0
			ORDER BY ov.value
		`, [])
		
		return result || []
	}

	/**
	 * Get B2B Root category ID by name
	 */
	static async getCategoryIdByName(metisId: string, categoryName: string): Promise<number> {
		const result = await metisTechnical.queryMetis(metisId, `
			SELECT entity_id
			FROM catalog_category_entity_varchar
			WHERE attribute_id = (
				SELECT attribute_id
				FROM eav_attribute
				WHERE attribute_code = 'name'
				AND entity_type_id = (
					SELECT entity_type_id
					FROM eav_entity_type
					WHERE entity_type_code = 'catalog_category'
				)
			)
			AND value = ?
			LIMIT 1
		`, [categoryName])

		if (!result || result.length === 0) {
			throw new Error(`Category '${categoryName}' not found for metisId: ${metisId}`)
		}

		return result[0].entity_id
	}

	/**
 	* Extract personal name from service provider contactInfos
	*/
	static async getPersonalNameFromServiceProvider(serviceProviderId: string): Promise<{ firstName: string, lastName: string }> {
		try {
			const serviceProvider = await cmClient.getEntry({
				content_type: 'serviceProvider',
				'sys.id': serviceProviderId,
				include: 2
			})

			if (!serviceProvider?.fields?.contactInfos?.de) {
				return { firstName: '', lastName: '' }
			}

			// Find the Name contact info
			const nameContactInfo = serviceProvider.fields.contactInfos.de.find(
				(ci: any) => ci?.fields?.contactInfoType?.de?.fields?.type?.de === ContactInfoType.NAME
			)

			if (!nameContactInfo?.fields?.contactDetail?.de) {
				// Fallback to contactPersonName if available
				const contactPersonName = serviceProvider.fields.contactPersonName?.de
				if (contactPersonName) {
					const nameParts = contactPersonName.split(' ')
					return {
						firstName: nameParts[0] || contactPersonName,
						lastName: nameParts.slice(1).join(' ') || ''
					}
				}
				return { firstName: '', lastName: '' }
			}

			const fullName = nameContactInfo.fields.contactDetail.de
			const nameParts = fullName.split(' ')
			
			return {
				firstName: nameParts[0] || fullName,
				lastName: nameParts.slice(1).join(' ') || ''
			}
		} catch (error) {
			console.warn(`Failed to get personal name from service provider ${serviceProviderId}: ${error.message}`)
			return { firstName: '', lastName: '' }
		}
	}

	private static readRef(entry: any, fieldName: string): any {
		return entry?.fields?.[fieldName]?.de ?? entry?.fields?.[fieldName] ?? null
	}

	private static readUserId(userRef: any): string | null {
		return userRef?.sys?.id ?? null
	}

	private static readUserEmail(userRef: any): string | null {
		const email = userRef?.fields?.email?.de
		return typeof email === 'string' && email.trim().length > 0 ? email.trim() : null
	}

	private static readMainUserId(userRef: any): string | null {
		const mainUserId = userRef?.fields?.mainUserId?.de
		return typeof mainUserId === 'string' && mainUserId.trim().length > 0 ? mainUserId.trim() : null
	}

	/**
	 * Resolve the main user (owner) for a given user id.
	 * If the user is invited (has mainUserId), the owner user is returned.
	 */
	static async getMainUserByUserId(userId: string): Promise<{ userId: string | null, email: string | null }> {
		try {
			if (!userId) {
				return { userId: null, email: null }
			}

			const user = await cmClient.getEntry({
				content_type: 'user',
				'sys.id': userId,
				include: 0
			})

			if (!user) {
				return { userId: null, email: null }
			}

			const linkedMainUserId = user?.fields?.mainUserId?.de
			if (linkedMainUserId) {
				const mainUser = await cmClient.getEntry({
					content_type: 'user',
					'sys.id': linkedMainUserId,
					include: 0
				})
				return {
					userId: mainUser?.sys?.id ?? linkedMainUserId,
					email: mainUser?.fields?.email?.de?.trim?.() ?? null
				}
			}

			return {
				userId: user?.sys?.id ?? userId,
				email: user?.fields?.email?.de?.trim?.() ?? null
			}
		} catch (error) {
			console.warn(`Failed to resolve main user by user id ${userId}: ${error.message}`)
			return { userId: null, email: null }
		}
	}

	/**
	 * Resolve the owner/main user id and email for a service provider.
	 * This should be used wherever we need the canonical login identity.
	 */
	static async getMainUserForServiceProvider(serviceProviderId: string): Promise<{ userId: string | null, email: string | null }> {
		try {
			if (!serviceProviderId) {
				return { userId: null, email: null }
			}

			const serviceProvider = await cmClient.getEntry({
				content_type: 'serviceProvider',
				'sys.id': serviceProviderId,
				include: 2
			})

			if (!serviceProvider) {
				return { userId: null, email: null }
			}

			const mainUserAccount = this.readRef(serviceProvider, 'mainUserAccount')
			const userAccount = this.readRef(serviceProvider, 'userAccount')

			let resolvedUserId =
				this.readUserId(mainUserAccount) ??
				this.readUserId(userAccount)
			let resolvedEmail =
				this.readUserEmail(mainUserAccount) ??
				this.readUserEmail(userAccount)

			// If we only have an invited user, jump to its main user.
			const nestedMainUserId =
				this.readMainUserId(mainUserAccount) ??
				this.readMainUserId(userAccount)
			if (nestedMainUserId) {
				resolvedUserId = nestedMainUserId
			}

			if (resolvedUserId) {
				const resolvedMain = await this.getMainUserByUserId(resolvedUserId)
				if (resolvedMain.userId || resolvedMain.email) {
					return {
						userId: resolvedMain.userId ?? resolvedUserId,
						email: resolvedMain.email ?? resolvedEmail ?? null
					}
				}
			}

			// Fallback for old/inconsistent data: find user by serviceProviderId.
			const users = await cmClient.getEntries({
				content_type: 'user',
				'fields.serviceProviderId': serviceProviderId,
				include: 0,
				limit: 50
			})

			if (users?.length) {
				const mainUser =
					users.find((user: any) => !user?.fields?.mainUserId?.de && user?.fields?.email?.de) ??
					users.find((user: any) => user?.fields?.email?.de)
				if (mainUser) {
					return {
						userId: mainUser?.sys?.id ?? null,
						email: mainUser?.fields?.email?.de?.trim?.() ?? null
					}
				}
			}

			const fallbackEmail = serviceProvider?.fields?.contactPersonEmail?.de?.trim?.() ?? null
			return {
				userId: null,
				email: fallbackEmail
			}
		} catch (error) {
			console.warn(`Failed to resolve main user for service provider ${serviceProviderId}: ${error.message}`)
			return { userId: null, email: null }
		}
	}

	static async getMainUserEmailFromServiceProvider(serviceProviderId: string): Promise<string | null> {
		const result = await this.getMainUserForServiceProvider(serviceProviderId)
		return result.email
	}

	static async getMainUserIdFromServiceProvider(serviceProviderId: string): Promise<string | null> {
		const result = await this.getMainUserForServiceProvider(serviceProviderId)
		return result.userId
	}
}
